import React from 'react';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ProtocolPage } from './ProtocolPage';
import { Modal } from '../../ui/Modal';

export function Protocol16_8() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isAbandonModalOpen, setIsAbandonModalOpen] = React.useState(false);
  const [activeFast, setActiveFast] = React.useState(null);
  const [stopReason, setStopReason] = React.useState('Hunger');

  // Check for active fast on mount
  React.useEffect(() => {
    const checkActiveFast = async () => {
      if (!sb || !currentUser?.id) return;

      try {
        const { data } = await sb
          .from('fasting_sessions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('status', 'active')
          .single();
        setActiveFast(data);
      } catch (error) {
        // No active fast found, which is fine
      }
    };

    checkActiveFast();
  }, [currentUser]);

  const handleStartProtocol = async () => {
    if (!sb || !currentUser?.id) {
      console.error('[Protocol16_8] No user or Supabase');
      return;
    }

    // Check if there's an active fast
    if (activeFast) {
      setIsAbandonModalOpen(true);
      return;
    }

    // No active fast, just switch protocol
    try {
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: '16:8',
        protocol_start_date: today,
      });
      console.log('[Protocol16_8] Protocol started: 16:8');
      navigate('/dashboard');
    } catch (error) {
      console.error('[Protocol16_8] Error starting protocol:', error);
    }
  };

  const handleConfirmAbandon = async () => {
    if (!sb || !currentUser?.id || !activeFast) {
      setIsAbandonModalOpen(false);
      return;
    }

    try {
      // Mark current fast as abandoned
      await sb.from('fasting_sessions').update({
        fast_end: new Date().toISOString(),
        status: 'abandoned',
        reason: stopReason,
      }).eq('id', activeFast.id);

      // Switch protocol
      const today = new Date().toISOString().split('T')[0];
      await sb.from('profiles').upsert({
        id: currentUser.id,
        current_protocol: '16:8',
        protocol_start_date: today,
      });

      setActiveFast(null);
      setIsAbandonModalOpen(false);
      console.log('[Protocol16_8] Fast abandoned, protocol set to 16:8');
      navigate('/dashboard');
    } catch (error) {
      console.error('[Protocol16_8] Error abandoning fast:', error);
    }
  };

  const biochemistryList = (
    <ul className="list-disc list-inside space-y-1">
      <li>Insulin and leptin lower during the extended fast</li>
      <li>Metabolic switch occurs between hours 12–16</li>
      <li>Liver glycogen depletes — body begins fat mobilisation</li>
      <li>Circadian alignment — peripheral molecular clocks synchronise</li>
    </ul>
  );

  return (
    <>
      <ProtocolPage
        protocolName="16:8"
        tagline="The Gateway"
        tierBadge="Free"
        whatIs="Time-restricted eating. Eat within an 8-hour window, fast for 16. The body's first introduction to metabolic flexibility."
        biochemistry={biochemistryList}
        whoItIsFor="Anyone starting their fasting journey. No blood work required. No gate."
        francoisSays="Every journey starts here. This is where the body learns that it can run without constant fuel."
        ctaText="Select Protocol"
        onCtaClick={handleStartProtocol}
      />

      {/* Abandon Modal */}
      <Modal
        isOpen={isAbandonModalOpen}
        onClose={() => setIsAbandonModalOpen(false)}
        title="Are you ending your fast early?"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-2">
              Reason:
            </label>
            <select
              value={stopReason}
              onChange={(e) => setStopReason(e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-on-surface focus:border-primary focus:outline-none"
            >
              <option>Hunger</option>
              <option>Medical</option>
              <option>Social</option>
              <option>Personal</option>
              <option>Other</option>
            </select>
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setIsAbandonModalOpen(false)}
              className="flex-1 rounded-lg bg-surface-container px-4 py-2 text-base font-medium text-on-surface hover:bg-surface-container-hover"
            >
              Continue Fasting
            </button>
            <button
              onClick={handleConfirmAbandon}
              className="flex-1 rounded-lg bg-error-container px-4 py-2 text-base font-medium text-error hover:bg-error-container/80"
            >
              Confirm Stop
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
